import { useEffect, useRef } from 'react';

/**
 * Custom hook for managing dependent form fields related to address,
 * specifically clearing 'state' and 'city' fields when the 'country' or 'state' changes.
 *
 * Behavior:
 * - When the countryCode changes, reset both state and city fields to null.
 * - When the stateCode changes, reset the city field to null.
 *
 * @param {string | undefined} countryCode - The current country code from the form.
 * @param {string | undefined} stateCode - The current state code from the form.
 * @param {string} stateFieldName - The name of the form field for state (e.g., 'shipperState').
 * @param {string} cityFieldName - The name of the form field for city (e.g., 'shipperCity').
 * @param {object} form - The Ant Design form instance with setFieldsValue method.
 */

export const useBehaviorForAddressFields = (countryCode, stateCode, stateFieldName, cityFieldName, form) => {
  const prevCountryCode = useRef(null);
  const prevStateCode = useRef(null);

  useEffect(() => {
    if (prevCountryCode.current !== undefined && prevCountryCode.current !== countryCode) {
      form?.setFieldsValue({ [stateFieldName]: null, [cityFieldName]: null });
    }
    prevCountryCode.current = countryCode;
  }, [countryCode, stateFieldName, cityFieldName, form]);

  useEffect(() => {
    if (prevStateCode.current && prevStateCode.current !== stateCode) {
      form?.setFieldsValue({ [cityFieldName]: null });
    }
    prevStateCode.current = stateCode;
  }, [stateCode, cityFieldName, form]);
};
